import { useCallback, useRef, useEffect } from "react";
import {
  DEFAULT_ANIMATION_DURATION,
  DEFAULT_TIMING_FUNCTION,
  CARD_ROTATION,
  PORTRAIT_CARD_OFFSET_X,
  PORTRAIT_CARD_OFFSET_Y,
  LANDSCAPE_CARD_OFFSET_X,
  LANDSCAPE_CARD_OFFSET_Y,
  DESKTOP_PORTRAIT_CARD_OFFSET_X,
  DESKTOP_PORTRAIT_CARD_OFFSET_Y,
  DESKTOP_LANDSCAPE_CARD_OFFSET_X,
  DESKTOP_LANDSCAPE_CARD_OFFSET_Y,
  PORTRAIT_FRONT_CARD_SENT_OFFSET_X,
  LANDSCAPE_FRONT_CARD_SENT_OFFSET_X,
  PORTRAIT_BACK_CARD_SENT_OFFSET_X,
  LANDSCAPE_BACK_CARD_SENT_OFFSET_X,
  DESKTOP_PORTRAIT_FRONT_CARD_SENT_OFFSET_X,
  DESKTOP_LANDSCAPE_FRONT_CARD_SENT_OFFSET_X,
  DESKTOP_PORTRAIT_BACK_CARD_SENT_OFFSET_X,
  DESKTOP_LANDSCAPE_BACK_CARD_SENT_OFFSET_X,
  BACK_CARD_RETURN_DURATION_MULTIPLIER,
  FRONT_CARD_SENT_DURATION_MULTIPLIER,
  CONFETTI_DURATION_MULTIPLIER,
  ANIMATION_COMPLETE_DURATION_MULTIPLIER,
  LANDSCAPE_WHOOSH_FINAL_X,
  PORTRAIT_WHOOSH_FINAL_X,
  LANDSCAPE_WHOOSH_INITIAL_X,
  PORTRAIT_WHOOSH_INITIAL_X,
  DESKTOP_LANDSCAPE_WHOOSH_FINAL_X,
  DESKTOP_PORTRAIT_WHOOSH_FINAL_X,
  DESKTOP_LANDSCAPE_WHOOSH_INITIAL_X,
  DESKTOP_PORTRAIT_WHOOSH_INITIAL_X,
  WHOOSH_SCALE,
  DESKTOP_WHOOSH_SCALE,
  DESKTOP_WIDTH
} from "../cardPreviewConfig";

interface CardRefs {
  frontRef: React.RefObject<HTMLDivElement | null>;
  backRef: React.RefObject<HTMLDivElement | null>;
  confettiRef: React.RefObject<HTMLDivElement | null>;
  whooshRef: React.RefObject<HTMLDivElement | null>;
}

interface CardDimensions {
  width: number;
  height: number;
}

interface AnimationConfig {
  duration?: number;
  timingFunction?: string;
  onComplete?: () => void;
}

interface UseCardAnimationProps {
  status: string;
  showCardBack: boolean;
  surveyCompleted: boolean;
  cardDimensions: CardDimensions;
  containerWidth: number;
  imageUrl?: string;
  refs: CardRefs;
  config?: AnimationConfig;
  imageLoaded?: boolean;
}

/**
 * Hook that handles card animation states and transitions
 * based on card status, dimensions, and device characteristics
 */
export const useCardAnimation = ({
  status,
  showCardBack,
  surveyCompleted,
  cardDimensions,
  containerWidth,
  imageUrl,
  refs,
  config = {},
  imageLoaded = false
}: UseCardAnimationProps) => {
  const { frontRef, backRef, confettiRef, whooshRef } = refs;
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Determine if we're in desktop mode based on window width
  const isDesktop = window.innerWidth >= DESKTOP_WIDTH;

  // Default animation configuration
  const animationDuration = config.duration || DEFAULT_ANIMATION_DURATION;
  const timingFunction = config.timingFunction || DEFAULT_TIMING_FUNCTION;

  // Creates responsive card transform with pixel-based positioning
  const createCardTransform = useCallback(
    (horizontalPercent: number, verticalOffset: number = 0, rotation: number = 0): string => {
      const pixelOffset = (horizontalPercent / 100) * containerWidth;
      return `translate(${pixelOffset}px, ${verticalOffset}px) rotate(${rotation}deg)`;
    },
    [containerWidth]
  );

  // Creates responsive transform for photo card states
  const createPhotoCardTransform = useCallback(
    (horizontalPercent: number, verticalPercent: number = 0): string => {
      const horizontalPixelOffset = (horizontalPercent / 100) * containerWidth;
      const verticalPixelOffset = (verticalPercent / 100) * containerWidth;
      return `translate(${horizontalPixelOffset}px, ${verticalPixelOffset}px)`;
    },
    [containerWidth]
  );

  // Creates transform for whoosh animation element with appropriate scaling
  const createWhooshTransform = useCallback(
    (horizontalPercent: number, scale: number = 1): string => {
      const pixelOffset = (horizontalPercent / 100) * containerWidth;
      return `translate(${pixelOffset}px, -50%) scale(${scale})`;
    },
    [containerWidth]
  );

  // Handles the complete card sending animation sequence
  // Creates a multi-stage animation that shows the card being flipped, rotated, and sent
  // with confetti and movement effects that are responsive to device size and orientation
  const animateSentCard = useCallback(() => {
    const frontCard = frontRef.current;
    const backCard = backRef.current;
    const confetti = confettiRef.current;
    const whoosh = whooshRef.current;

    if (!frontCard || !backCard || !confetti || !whoosh) return;

    // Determine if card is in landscape orientation
    const isLandscape = cardDimensions.width / cardDimensions.height >= 1;

    // Select appropriate scale and position values based on device and orientation
    const whooshScale = isDesktop ? DESKTOP_WHOOSH_SCALE : WHOOSH_SCALE;
    const baseLandscapeFinalX = isDesktop
      ? DESKTOP_LANDSCAPE_WHOOSH_FINAL_X
      : LANDSCAPE_WHOOSH_FINAL_X;
    const basePortraitFinalX = isDesktop
      ? DESKTOP_PORTRAIT_WHOOSH_FINAL_X
      : PORTRAIT_WHOOSH_FINAL_X;
    const baseLandscapeInitialX = isDesktop
      ? DESKTOP_LANDSCAPE_WHOOSH_INITIAL_X
      : LANDSCAPE_WHOOSH_INITIAL_X;
    const basePortraitInitialX = isDesktop
      ? DESKTOP_PORTRAIT_WHOOSH_INITIAL_X
      : PORTRAIT_WHOOSH_INITIAL_X;

    const whooshFinalX = isLandscape ? baseLandscapeFinalX : basePortraitFinalX;
    const whooshInitialX = isLandscape ? baseLandscapeInitialX : basePortraitInitialX;

    // Initial element positioning
    if (!showCardBack) {
      backCard.style.opacity = "0";
      frontCard.style.transform = "translate(0, 0)";
      frontCard.style.zIndex = "2";
    } else {
      // Select offsets based on device type and orientation
      let offsetX, offsetY;

      if (isDesktop) {
        offsetX = isLandscape ? DESKTOP_LANDSCAPE_CARD_OFFSET_X : DESKTOP_PORTRAIT_CARD_OFFSET_X;
        offsetY = isLandscape ? DESKTOP_LANDSCAPE_CARD_OFFSET_Y : DESKTOP_PORTRAIT_CARD_OFFSET_Y;
      } else {
        offsetX = isLandscape ? LANDSCAPE_CARD_OFFSET_X : PORTRAIT_CARD_OFFSET_X;
        offsetY = isLandscape ? LANDSCAPE_CARD_OFFSET_Y : PORTRAIT_CARD_OFFSET_Y;
      }

      frontCard.style.transform = createCardTransform(-offsetX, -offsetY);
      backCard.style.transform = createCardTransform(offsetX, offsetY);
      frontCard.style.zIndex = "1";
      backCard.style.zIndex = "2";
    }

    whoosh.style.opacity = "0";
    whoosh.style.transform = createWhooshTransform(whooshInitialX, whooshScale);
    confetti.style.transform = "scale(0.001)";
    confetti.style.opacity = "0";

    // Animation sequence with improved smoothness
    const sequence = async () => {
      // Initialize animation when back card isn't already visible
      if (!showCardBack) {
        // Ensure cards are fully visible before animation
        frontCard.style.opacity = "1";
        backCard.style.opacity = "1";

        // Set z-indices with a small delay to ensure smooth transition
        setTimeout(() => {
          frontCard.style.zIndex = "1";
          backCard.style.zIndex = "2";
        }, 10);
      }

      // 1. Move back card to the right and rotate - with smoother transitions
      await new Promise((resolve) => {
        let verticalOffset;

        if (isDesktop) {
          verticalOffset = isLandscape
            ? DESKTOP_LANDSCAPE_CARD_OFFSET_Y
            : DESKTOP_PORTRAIT_CARD_OFFSET_Y;
        } else {
          verticalOffset = isLandscape ? LANDSCAPE_CARD_OFFSET_Y : PORTRAIT_CARD_OFFSET_Y;
        }

        let frontOffset, backOffset;

        if (isDesktop) {
          frontOffset = isLandscape
            ? DESKTOP_LANDSCAPE_FRONT_CARD_SENT_OFFSET_X
            : DESKTOP_PORTRAIT_FRONT_CARD_SENT_OFFSET_X;
          backOffset = isLandscape
            ? DESKTOP_LANDSCAPE_BACK_CARD_SENT_OFFSET_X
            : DESKTOP_PORTRAIT_BACK_CARD_SENT_OFFSET_X;
        } else {
          frontOffset = isLandscape
            ? LANDSCAPE_FRONT_CARD_SENT_OFFSET_X
            : PORTRAIT_FRONT_CARD_SENT_OFFSET_X;
          backOffset = isLandscape
            ? LANDSCAPE_BACK_CARD_SENT_OFFSET_X
            : PORTRAIT_BACK_CARD_SENT_OFFSET_X;
        }

        // Use requestAnimationFrame for smoother animation queuing
        requestAnimationFrame(() => {
          frontCard.style.transform = createCardTransform(-frontOffset, 0);
          backCard.style.transform = createCardTransform(backOffset, verticalOffset, CARD_ROTATION);
        });

        setTimeout(resolve, animationDuration + 50); // Add small buffer for animation completion
      });

      // 2. Switch card stacking order for return animation - with better transition timing
      await new Promise((resolve) => {
        // Use a brief timeout to ensure transform completes before z-index change
        setTimeout(() => {
          backCard.style.zIndex = "1";
          frontCard.style.zIndex = "2";
          resolve(null);
        }, 50); // Small delay to ensure transforms complete first
      });

      // 3. Return cards to center with rotation - improved smoothness
      await new Promise((resolve) => {
        let frontOffset;

        if (isDesktop) {
          frontOffset = isLandscape
            ? DESKTOP_LANDSCAPE_FRONT_CARD_SENT_OFFSET_X
            : DESKTOP_PORTRAIT_FRONT_CARD_SENT_OFFSET_X;
        } else {
          frontOffset = isLandscape
            ? LANDSCAPE_FRONT_CARD_SENT_OFFSET_X
            : PORTRAIT_FRONT_CARD_SENT_OFFSET_X;
        }

        // Use requestAnimationFrame for smoother animation
        requestAnimationFrame(() => {
          backCard.style.transform = createCardTransform(-frontOffset, 0, 0);
        });

        setTimeout(resolve, animationDuration * BACK_CARD_RETURN_DURATION_MULTIPLIER + 50);
      });

      // 4. Hide back card only after its animation completes - smoother fade
      await new Promise((resolve) => {
        // Use requestAnimationFrame for smoother animation
        requestAnimationFrame(() => {
          backCard.style.opacity = "0";
        });

        setTimeout(resolve, animationDuration + 20);
      });

      // 5. Move front card and whoosh to the right and show confetti - smoother animation
      await new Promise((resolve) => {
        let frontOffset;

        if (isDesktop) {
          frontOffset = isLandscape
            ? DESKTOP_LANDSCAPE_FRONT_CARD_SENT_OFFSET_X
            : DESKTOP_PORTRAIT_FRONT_CARD_SENT_OFFSET_X;
        } else {
          frontOffset = isLandscape
            ? LANDSCAPE_FRONT_CARD_SENT_OFFSET_X
            : PORTRAIT_FRONT_CARD_SENT_OFFSET_X;
        }

        // Use requestAnimationFrame to ensure smooth animation
        requestAnimationFrame(() => {
          // Apply proper transitions with the correct duration multipliers
          frontCard.style.transition = `transform ${animationDuration * FRONT_CARD_SENT_DURATION_MULTIPLIER}ms ${timingFunction}, opacity ${animationDuration * FRONT_CARD_SENT_DURATION_MULTIPLIER}ms ease-out`;
          whoosh.style.transition = `transform ${animationDuration * FRONT_CARD_SENT_DURATION_MULTIPLIER}ms ${timingFunction}, opacity ${animationDuration * FRONT_CARD_SENT_DURATION_MULTIPLIER}ms ease-out`;

          // Set the card and whoosh transforms in a single frame
          frontCard.style.transform = createCardTransform(frontOffset, 0, 0);
          whoosh.style.opacity = "1";
          whoosh.style.transform = createWhooshTransform(whooshFinalX, whooshScale);

          // Delay the confetti just slightly for visual interest
          setTimeout(() => {
            confetti.style.transform = "scale(1)";
            confetti.style.opacity = "1";
          }, 100);
        });

        // Extended timeout for smoother completion
        animationTimeoutRef.current = setTimeout(() => {
          config.onComplete?.();
          resolve(null);
        }, animationDuration * ANIMATION_COMPLETE_DURATION_MULTIPLIER);
      });
    };

    sequence();
  }, [
    frontRef,
    backRef,
    confettiRef,
    whooshRef,
    cardDimensions,
    showCardBack,
    createCardTransform,
    createWhooshTransform,
    config,
    animationDuration,
    timingFunction,
    isDesktop
  ]);

  // Animation for photo/questionnaire states
  const animatePhotoCard = useCallback(() => {
    const frontCard = frontRef.current;
    const backCard = backRef.current;

    // Determine if device is desktop and get dimensions
    const isDesktopDevice = window.innerWidth >= DESKTOP_WIDTH;
    const isLandscapeOrientation = cardDimensions.width / cardDimensions.height >= 1;

    // Select offsets based on device type and orientation
    let offsetX, offsetY;

    if (isDesktopDevice) {
      offsetX = isLandscapeOrientation
        ? DESKTOP_LANDSCAPE_CARD_OFFSET_X
        : DESKTOP_PORTRAIT_CARD_OFFSET_X;
      offsetY = isLandscapeOrientation
        ? DESKTOP_LANDSCAPE_CARD_OFFSET_Y
        : DESKTOP_PORTRAIT_CARD_OFFSET_Y;
    } else {
      offsetX = isLandscapeOrientation ? LANDSCAPE_CARD_OFFSET_X : PORTRAIT_CARD_OFFSET_X;
      offsetY = isLandscapeOrientation ? LANDSCAPE_CARD_OFFSET_Y : PORTRAIT_CARD_OFFSET_Y;
    }

    if (!frontCard || !backCard) return;

    frontCard.style.transform = createPhotoCardTransform(-offsetX, -offsetY);
    backCard.style.transform = createPhotoCardTransform(offsetX, offsetY);
    backCard.style.opacity = "1";

    // Update z-index based on questionnaire state
    if (status === "questionnaire") {
      frontCard.style.zIndex = "1";
      backCard.style.zIndex = "2";
    } else {
      frontCard.style.zIndex = "2";
      backCard.style.zIndex = "1";
    }
  }, [frontRef, backRef, status, createPhotoCardTransform, cardDimensions]);

  // Sets card positions for default state
  const animateDefaultCard = useCallback(() => {
    const frontCard = frontRef.current;
    const backCard = backRef.current;

    if (!frontCard || !backCard) return;

    frontCard.style.transform = "translate(0, 0)";
    frontCard.style.zIndex = "2";
    backCard.style.transform = "translate(0, 0)";
    backCard.style.zIndex = "1";
    backCard.style.opacity = "1";
  }, [frontRef, backRef]); // Only depends on refs, not timing

  // Track whether we've run the sent animation already
  const sentAnimationRun = useRef(false);
  // Track previous status to detect changes
  const previousStatus = useRef<string>(status);

  // Apply appropriate animation based on status
  useEffect(() => {
    if (
      frontRef.current &&
      backRef.current &&
      confettiRef.current &&
      whooshRef.current &&
      cardDimensions.width > 0 &&
      cardDimensions.height > 0
    ) {
      const frontCard = frontRef.current;
      const backCard = backRef.current;
      const confetti = confettiRef.current;
      const whoosh = whooshRef.current;

      // Set up transitions with better properties for smoother animations
      const customBezier = timingFunction;
      frontCard.style.transition = `transform ${animationDuration}ms ${customBezier}, opacity ${animationDuration}ms ease-out, z-index 0s`;
      frontCard.style.willChange = "transform, opacity, z-index"; // Hint to browser to optimize these properties

      confetti.style.transition = `transform ${animationDuration * CONFETTI_DURATION_MULTIPLIER}ms ease-out, opacity ${animationDuration * CONFETTI_DURATION_MULTIPLIER}ms ease-out`;
      confetti.style.willChange = "transform, opacity";

      whoosh.style.transition = `transform ${animationDuration}ms ${customBezier}, opacity ${animationDuration}ms ease-out`;
      whoosh.style.willChange = "transform, opacity";

      backCard.style.transition = `transform ${animationDuration}ms ${customBezier}, opacity ${animationDuration}ms ease-out, z-index 0s`;
      backCard.style.willChange = "transform, opacity, z-index";

      // Clear any existing animation timeout
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = undefined;
      }

      // Hide confetti when not sending card
      if (status !== "sent") {
        confetti.style.transform = "scale(0.001)";
        confetti.style.opacity = "0";
        // Reset animation run flag when status changes away from "sent"
        // This allows re-running the animation when switching back to sent
        sentAnimationRun.current = false;

        // If switching to questionnaire, make sure we position cards properly for animation
        if (status === "questionnaire") {
          // Force position to questionnaire layout
          animatePhotoCard();
        }
      }

      // Determine which animation to run based on status
      if (status === "sent") {
        // If there's an image URL but it hasn't loaded yet, postpone animation
        if (imageUrl && !imageLoaded) {
          // Pre-position cards in "questionnaire" position, ready for animation
          animatePhotoCard();
          return; // Skip animation until image is loaded
        }

        // Only run the sent animation if we haven't run it yet,
        // or if the status just changed from something else to "sent"
        if (!sentAnimationRun.current || previousStatus.current !== "sent") {
          // Make sure to reset card positions first if coming from another state
          if (previousStatus.current !== "questionnaire") {
            // Briefly apply questionnaire positioning first to ensure proper start position
            animatePhotoCard();
            // Small timeout to ensure positions are applied before animation
            setTimeout(() => {
              animateSentCard();
            }, 50);
          } else {
            // If already properly positioned, just run the animation
            animateSentCard();
          }
          sentAnimationRun.current = true;
        }
        previousStatus.current = status;
      } else if (
        status === "photo-pending" ||
        status === "photo-review" ||
        status === "photo-rejected" ||
        status === "questionnaire"
      ) {
        animatePhotoCard();
      } else {
        animateDefaultCard();
      }
    }
  }, [
    showCardBack,
    status,
    surveyCompleted,
    cardDimensions,
    imageUrl,
    imageLoaded,
    frontRef,
    backRef,
    confettiRef,
    whooshRef,
    animateSentCard,
    animatePhotoCard,
    animateDefaultCard,
    animationDuration,
    timingFunction
  ]);

  // We no longer need a separate effect for imageLoaded changes
  // as the main effect above now handles the imageLoaded state properly

  // Cleanup function for animation timeouts
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);
};

export default useCardAnimation;
